import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import type {
  CoachingApprovalFields,
  CoachingApplicationDraft,
  CoachingVideoReference,
} from "@/lib/types";
import {
  isCurrentDraftContext,
  type DraftContext,
} from "@/features/coach-review/composerDrafts";
import { decodePrivateFeedbackReviewCarry, type PrivateFeedbackReviewCarry } from "./privateFeedbackReview";
import { decodeCanonicalFeedbackReviewCarry, type CanonicalReviewCarryFields } from "./canonicalFeedbackReview";
export const feedbackFields = [
  "title",
  "body",
  "player_summary",
  "what_happened",
  "why_it_matters",
  "what_to_do_next",
] as const;
export const actionFields = [
  "title",
  "description",
  "success_criteria",
] as const;
export type HumanEdits = Partial<CoachingApprovalFields>;
export type ReviewOrigin = {
  captureId: string;
  draftId: string;
  draftRevision: number;
  confirmationId: string;
  confirmationRevision: number;
};
export type ReviewCarry = CanonicalReviewCarryFields & {
  kind: "feedback" | "action";
  origin: ReviewOrigin;
  edits: HumanEdits;
  video?: CoachingVideoReference;
  privateFeedback?: PrivateFeedbackReviewCarry;
};
export function proposedWriting(
  draft: CoachingApplicationDraft,
): CoachingApprovalFields {
  const empty = {
    title: "",
    body: "",
    player_summary: "",
    what_happened: "",
    why_it_matters: "",
    what_to_do_next: "",
    description: "",
    success_criteria: "",
  };
  // Canonical Feedback preparation starts with explicit human writing. Its
  // proposal is reference context, never converted into legacy writing fields.
  const fields = draft.proposed_fields;
  return draft.kind === 'action' && 'title' in fields
    ? { ...empty, title: fields.title, description: fields.description,
        success_criteria: fields.success_criteria }
    : empty;
}
/** Only fields independently touched by the human, never an untouched proposal. */
export function boundedHumanEdits(
  dirty: HumanEdits,
  kind: "feedback" | "action",
): HumanEdits {
  const allowed = kind === "feedback" ? feedbackFields : actionFields;
  const result: HumanEdits = {};
  for (const key of allowed) {
    const value = dirty[key];
    if (typeof value === "string")
      result[key] = value.slice(
        0,
        key === "title" ? 160 : key === "body" ? 4000 : 2000,
      );
  }
  return result;
}
const carryOwners = new Map<string, DraftContext>();
const retiredCarries = new Map<string, DraftContext>();
function removeDepartedCarries() {
  for (const [storageKey, owner] of retiredCarries) {
    if (!isCurrentDraftContext(owner)) retiredCarries.delete(storageKey);
  }
  for (const [storageKey, owner] of carryOwners) {
    if (!isCurrentDraftContext(owner)) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* unavailable */
      }
      carryOwners.delete(storageKey);
    }
  }
}
useAuthStore.subscribe(removeDepartedCarries);
useScopeStore.subscribe(removeDepartedCarries);
function key(owner: DraftContext) {
  return `tktic_cr_draft:inbox:${JSON.stringify([owner.actorId, owner.clubId, owner.teamId, owner.seasonId])}`;
}
function selectionKey(owner: DraftContext) {
  return `${key(owner).replace(':inbox:', ':inbox-selection:')}`;
}
function itemKey(owner: DraftContext, origin: { captureId: string; draftId: string }) {
  return `${key(owner)}:${JSON.stringify([origin.captureId, origin.draftId])}`;
}
function storedKeys(owner: DraftContext): string[] {
  return Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i)!)
    .filter(storageKey => storageKey === key(owner) || storageKey.startsWith(`${key(owner)}:`));
}
export function saveReviewCarry(owner: DraftContext, carry: ReviewCarry): boolean {
  if (!isCurrentDraftContext(owner)) return false;
  const storageKey = itemKey(owner, carry.origin);
  if (retiredCarries.has(`${owner.generation}:${storageKey}`)) return false;
  const latest = readReviewCarry(owner, carry.origin);
  const sameOrigin = latest && JSON.stringify(latest.origin) === JSON.stringify(carry.origin);
  // A stale form cannot drop either domain's unresolved native attempt or move
  // its attachment to a different draft/confirmation revision.
  if (latest && !sameOrigin && (latest.privateFeedback || latest.canonicalFeedback)) {
    if (latest.privateFeedback?.recording_entry_id || latest.canonicalFeedback?.recording_entry_id
        || latest.privateFeedback && !carry.privateFeedback || latest.canonicalFeedback && !carry.canonicalFeedback) return false;
  }
  const privateValue = carry.privateFeedback ?? (sameOrigin ? latest.privateFeedback : undefined);
  const publicValue = carry.canonicalFeedback ?? (sameOrigin ? latest.canonicalFeedback : undefined);
  const privateFeedback = privateValue === undefined ? undefined : decodePrivateFeedbackReviewCarry(privateValue);
  const canonicalFeedback = publicValue === undefined ? undefined : decodeCanonicalFeedbackReviewCarry(publicValue);
  if (privateFeedback === null || canonicalFeedback === null) return false;
  const recordingIntent = carry.recordingIntent ?? (sameOrigin ? latest.recordingIntent : undefined);
  if (recordingIntent !== undefined && recordingIntent !== 'private' && recordingIntent !== 'public') return false;
  carryOwners.set(storageKey, owner);
  carryOwners.set(selectionKey(owner), owner);
  try {
    const serialized = JSON.stringify({
        kind: carry.kind,
        origin: {
          captureId: carry.origin.captureId,
          draftId: carry.origin.draftId,
          draftRevision: carry.origin.draftRevision,
          confirmationId: carry.origin.confirmationId,
          confirmationRevision: carry.origin.confirmationRevision,
        },
        edits: boundedHumanEdits(carry.edits, carry.kind),
        ...(privateFeedback ? { privateFeedback } : {}),
        ...(canonicalFeedback ? { canonicalFeedback } : {}),
        ...(recordingIntent ? { recordingIntent } : {}),
        ...(validVideo(carry.video)
          ? {
              video: {
                match_id: carry.video.match_id,
                slot: carry.video.slot,
                timestamp_seconds: carry.video.timestamp_seconds,
                match_slug: carry.video.match_slug,
                reference_revision: carry.video.reference_revision,
              },
            }
          : {}),
      });
    sessionStorage.setItem(storageKey, serialized);
    sessionStorage.setItem(selectionKey(owner), storageKey);
    return isCurrentDraftContext(owner)
      && sessionStorage.getItem(storageKey) === serialized
      && sessionStorage.getItem(selectionKey(owner)) === storageKey;
  } catch {
    return false;
  }
}
export function clearReviewCarry(owner: DraftContext): void {
  try {
    for (const storageKey of [...storedKeys(owner), selectionKey(owner)]) {
      sessionStorage.removeItem(storageKey);
      carryOwners.delete(storageKey);
    }
  } catch {
    /* unavailable */
  }
}
export function clearReviewCarryForOrigin(
  owner: DraftContext,
  origin: { captureId: string; draftId: string },
): void {
  retiredCarries.set(`${owner.generation}:${itemKey(owner, origin)}`, owner);
  const carry = readReviewCarry(owner, origin);
  if (
    carry?.origin.captureId === origin.captureId &&
    carry.origin.draftId === origin.draftId
  )
    try {
      const storageKey = itemKey(owner, origin);
      sessionStorage.removeItem(storageKey);
      carryOwners.delete(storageKey);
      // Also erase a legacy, single-slot carry only when it has this origin.
      const legacy = sessionStorage.getItem(key(owner));
      if (legacy && JSON.parse(legacy).origin?.captureId === origin.captureId && JSON.parse(legacy).origin?.draftId === origin.draftId)
        sessionStorage.removeItem(key(owner));
      if (sessionStorage.getItem(selectionKey(owner)) === storageKey)
        sessionStorage.removeItem(selectionKey(owner));
    } catch { /* unavailable */ }
}
export function clearReviewCarryForCapture(owner: DraftContext, captureId: string): void {
  try {
    for (const storageKey of storedKeys(owner)) {
      const value = JSON.parse(sessionStorage.getItem(storageKey) ?? 'null') as ReviewCarry | null;
      if (value?.origin?.captureId === captureId) clearReviewCarryForOrigin(owner, value.origin);
    }
  } catch { /* unavailable */ }
}
export function readReviewCarry(owner: DraftContext, origin?: { captureId: string; draftId?: string }): ReviewCarry | null {
  if (!isCurrentDraftContext(owner)) return null;
  try {
    const keys = storedKeys(owner);
    // Register every restored item for immediate scope/logout/access cleanup.
    for (const storageKey of [...keys, selectionKey(owner)]) carryOwners.set(storageKey, owner);
    const selectedKey = sessionStorage.getItem(selectionKey(owner));
    const matches = (candidate: string) => {
      if (!origin) return false;
      const value = JSON.parse(sessionStorage.getItem(candidate) ?? 'null') as ReviewCarry | null;
      return value?.origin?.captureId === origin.captureId && (!origin.draftId || value.origin.draftId === origin.draftId);
    };
    const storageKey = origin ? selectedKey && keys.includes(selectedKey) && matches(selectedKey) ? selectedKey : [...keys].reverse().find(matches) : selectedKey && keys.includes(selectedKey) ? selectedKey : keys.at(-1);
    const carry: unknown = JSON.parse(
      (storageKey && sessionStorage.getItem(storageKey)) || "null",
    );
    if (
      !carry ||
      typeof carry !== "object" ||
      !("origin" in carry) ||
      !("edits" in carry)
    )
      return null;
    const value = carry as ReviewCarry;
    const privateFeedback = value.privateFeedback === undefined
      ? undefined : decodePrivateFeedbackReviewCarry(value.privateFeedback);
    const canonicalFeedback = value.canonicalFeedback === undefined ? undefined : decodeCanonicalFeedbackReviewCarry(value.canonicalFeedback);
    if (privateFeedback === null || canonicalFeedback === null
        || value.recordingIntent !== undefined && value.recordingIntent !== 'private' && value.recordingIntent !== 'public') return null;
    const o = value.origin;
    if (
      !o ||
      ![o.captureId, o.draftId, o.confirmationId].every(
        (id) => typeof id === "string" && id.length > 0 && id.length <= 120,
      ) ||
      !Number.isInteger(o.draftRevision) ||
      o.draftRevision < 1 ||
      !Number.isInteger(o.confirmationRevision) ||
      o.confirmationRevision < 1
    )
      return null;
    return {
      kind: value.kind === "action" ? "action" : "feedback",
      origin: {
        captureId: o.captureId,
        draftId: o.draftId,
        draftRevision: o.draftRevision,
        confirmationId: o.confirmationId,
        confirmationRevision: o.confirmationRevision,
      },
      edits: boundedHumanEdits(
        value.edits,
        value.kind === "action" ? "action" : "feedback",
      ),
      ...(validVideo(value.video) ? { video: value.video } : {}),
      ...(privateFeedback ? { privateFeedback } : {}),
      ...(canonicalFeedback ? { canonicalFeedback } : {}),
      ...(value.recordingIntent ? { recordingIntent: value.recordingIntent } : {}),
    };
  } catch {
    return null;
  }
}
/** Unknown acknowledgement freezes the operation body. New edits require explicit reset. */
export class FrozenOperation<T> {
  private request: T | null = null;
  begin(make: () => T): T {
    this.request ??= structuredClone(make());
    return structuredClone(this.request);
  }
  reset(): void {
    this.request = null;
  }
  get pending(): boolean {
    return this.request !== null;
  }
}
export function recipientsReviewable(
  recipients: Array<{
    user_id: string;
    display_name: string | null;
    label_status: string;
  }>,
  ids: string[],
): boolean {
  return (
    recipients.length === ids.length &&
    recipients.every(
      (r, index) =>
        r.user_id === ids[index] &&
        r.label_status === "available" &&
        !!r.display_name?.trim(),
    )
  );
}

function validVideo(value: unknown): value is CoachingVideoReference {
  if (!value || typeof value !== "object") return false;
  const v = value as CoachingVideoReference;
  return (
    typeof v.match_id === "string" &&
    v.match_id.length <= 120 &&
    typeof v.match_slug === "string" &&
    v.match_slug.length <= 200 &&
    [
      "full",
      "first_half",
      "second_half",
      "extra_time",
      "penalty_shootout",
    ].includes(v.slot) &&
    Number.isFinite(v.timestamp_seconds) &&
    v.timestamp_seconds >= 0 &&
    typeof v.reference_revision === "string" &&
    /^[a-f0-9]{64}$/.test(v.reference_revision)
  );
}
